import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useLocation as useLocationContext } from '../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import { useAuth } from '../../contexts/AuthContext';
import apiClient, { getErrorMessage } from '../../utils/api';
import { AdvancePayment as AdvancePaymentType, AdvancePaymentStatus, Employee } from '../../types/api';
import { PlusIcon, XCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface AdvancePaymentFormData {
  employee_id: string;
  amount: number;
  advance_date: string;
  reason?: string;
}

const advanceSchema = yup.object({
  employee_id: yup.string().required('Employee is required'),
  amount: yup.number().required('Amount is required').positive('Amount must be positive'),
  advance_date: yup.string().required('Date is required'),
  reason: yup.string().optional(),
});

const AdvancePaymentPage: React.FC = () => {
  const { selectedLocation } = useLocationContext();
  const { businessType } = useBusinessTypeContext();
  const { user } = useAuth();
  const [advances, setAdvances] = useState<AdvancePaymentType[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | AdvancePaymentStatus>('all');

  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin';

  const form = useForm<AdvancePaymentFormData>({
    resolver: yupResolver(advanceSchema) as any,
    defaultValues: {
      employee_id: '',
      amount: 0,
      advance_date: new Date().toISOString().split('T')[0],
      reason: '',
    },
  });

  useEffect(() => {
    if (selectedLocation) {
      fetchAdvances();
      fetchEmployees();
    }
  }, [selectedLocation, businessType, statusFilter]);

  const fetchEmployees = async () => {
    if (!selectedLocation) return;
    try {
      const response = await apiClient.get<Employee[]>(`/employees?location_id=${selectedLocation.id}&business_type=${businessType}&is_active=true`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchAdvances = async () => {
    if (!selectedLocation) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        location_id: selectedLocation.id,
        ...(statusFilter !== 'all' && { status: statusFilter }),
      });
      const response = await apiClient.get<AdvancePaymentType[]>(`/advance-payments?${params.toString()}`);
      setAdvances(response.data);
    } catch (error) {
      console.error('Error fetching advances:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: AdvancePaymentFormData) => {
    if (!selectedLocation) {
      setFormError('Please select a location');
      return;
    }

    setFormError('');
    setSubmitting(true);

    try {
      await apiClient.post('/advance-payments', {
        employee_id: data.employee_id,
        location_id: selectedLocation.id,
        business_type: businessType,
        amount: data.amount,
        advance_date: data.advance_date,
        reason: data.reason || undefined,
      });

      setShowForm(false);
      form.reset({
        employee_id: '',
        amount: 0,
        advance_date: new Date().toISOString().split('T')[0],
        reason: '',
      });
      fetchAdvances();
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Failed to create advance payment');
      setFormError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (advanceId: string) => {
    if (!window.confirm('Are you sure you want to cancel this advance payment?')) return;
    
    try {
      await apiClient.put(`/advance-payments/${advanceId}/cancel`);
      fetchAdvances();
    } catch (error: any) {
      alert(getErrorMessage(error, 'Failed to cancel advance payment'));
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setFormError('');
    form.reset({
      employee_id: '',
      amount: 0,
      advance_date: new Date().toISOString().split('T')[0],
      reason: '',
    });
  };

  const getStatusColor = (status: AdvancePaymentStatus) => {
    switch (status) {
      case 'deducted':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!selectedLocation) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">Please select a location to view advance payments.</p>
        </div>
      </div>
    );
  }

  const filteredAdvances = statusFilter === 'all' ? advances : advances.filter(a => a.status === statusFilter);

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Advance Payment</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Manage advance payments to employees</p>
        </div>
        {isManagerOrAdmin && (
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2 w-full sm:w-auto"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Add Advance</span>
          </button>
        )}
      </div>

      {/* Filter */}
      <div className="mb-4 flex flex-wrap gap-2 overflow-x-auto pb-2 -mx-3 px-3 sm:mx-0 sm:px-0">
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap ${
            statusFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All
        </button>
        <button
          onClick={() => setStatusFilter('pending')}
          className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap ${
            statusFilter === 'pending' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Pending
        </button>
        <button
          onClick={() => setStatusFilter('deducted')}
          className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap ${
            statusFilter === 'deducted' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Deducted
        </button>
        <button
          onClick={() => setStatusFilter('cancelled')}
          className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap ${
            statusFilter === 'cancelled' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Cancelled
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deducted From</th>
                    {isManagerOrAdmin && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAdvances.map((advance) => (
                    <tr key={advance.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {advance.employee_name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(advance.advance_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        ₹{advance.amount.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{advance.reason || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(advance.status)}`}>
                          {advance.status.charAt(0).toUpperCase() + advance.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {advance.deducted_from_salary_id ? (
                          <span className="text-xs text-blue-600">Salary #{advance.deducted_from_salary_id.slice(0, 8)}...</span>
                        ) : (
                          <span className="text-xs text-gray-400">Not deducted</span>
                        )}
                      </td>
                      {isManagerOrAdmin && advance.status === 'pending' && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleCancel(advance.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Cancel"
                          >
                            <XCircleIcon className="h-5 w-5" />
                          </button>
                        </td>
                      )}
                      {isManagerOrAdmin && advance.status !== 'pending' && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">-</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredAdvances.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <p>No advance payments found. {isManagerOrAdmin && 'Click "Add Advance" to create one.'}</p>
              </div>
            )}
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {filteredAdvances.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
                <p>No advance payments found. {isManagerOrAdmin && 'Click "Add Advance" to create one.'}</p>
              </div>
            ) : (
              filteredAdvances.map((advance) => (
                <div key={advance.id} className="bg-white rounded-lg shadow p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1">
                        {advance.employee_name || 'N/A'}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {new Date(advance.advance_date).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(advance.status)}`}>
                        {advance.status.charAt(0).toUpperCase() + advance.status.slice(1)}
                      </span>
                      {isManagerOrAdmin && advance.status === 'pending' && (
                        <button
                          onClick={() => handleCancel(advance.id)}
                          className="text-red-600 hover:text-red-900 p-1"
                          title="Cancel"
                        >
                          <XCircleIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">Amount</span>
                      <span className="text-sm font-semibold text-gray-900">₹{advance.amount.toFixed(2)}</span>
                    </div>
                    {advance.reason && (
                      <div>
                        <span className="text-xs text-gray-500">Reason:</span>
                        <p className="text-xs text-gray-700 mt-1">{advance.reason}</p>
                      </div>
                    )}
                    <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                      <span className="text-xs text-gray-500">Deducted From</span>
                      <span className="text-xs">
                        {advance.deducted_from_salary_id ? (
                          <span className="text-blue-600">Salary #{advance.deducted_from_salary_id.slice(0, 8)}...</span>
                        ) : (
                          <span className="text-gray-400">Not deducted</span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* Add Advance Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-xl font-bold">Add Advance Payment</h2>
              <button
                onClick={handleCloseForm}
                className="text-gray-400 hover:text-gray-600 p-1"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>

            {formError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
                <p className="text-red-800 text-xs sm:text-sm">{formError}</p>
              </div>
            )}

            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Employee */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...form.register('employee_id')}
                    className={`w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      form.formState.errors.employee_id ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select employee</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name} ({emp.employee_code})
                      </option>
                    ))}
                  </select>
                  {form.formState.errors.employee_id && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">{form.formState.errors.employee_id.message}</p>
                  )}
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    {...form.register('amount', { valueAsNumber: true })}
                    className={`w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      form.formState.errors.amount ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0.00"
                  />
                  {form.formState.errors.amount && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">{form.formState.errors.amount.message}</p>
                  )}
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Advance Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    {...form.register('advance_date')}
                    className={`w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      form.formState.errors.advance_date ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {form.formState.errors.advance_date && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600">{form.formState.errors.advance_date.message}</p>
                  )}
                </div>

                {/* Reason */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason
                  </label>
                  <input
                    type="text"
                    {...form.register('reason')}
                    className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional reason"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-blue-800">
                  <strong>Note:</strong> Advance payments will be automatically deducted from the next salary generation for this employee.
                </p>
              </div>

              {/* Submit Buttons */}
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:space-x-3 sm:gap-0 pt-4 border-t mt-6">
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="w-full sm:w-auto px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto px-4 py-2 text-sm sm:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Creating...</span>
                    </>
                  ) : (
                    <span>Create Advance</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancePaymentPage;
